import {
  Children,
  cloneElement,
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  HTMLAttributes,
  isValidElement,
  ReactElement,
  ReactNode,
} from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';

import { cn } from '../../helpers/cn';
import { renderDefaultHeader } from './common';

const {
  Root: DrawerRoot,
  Overlay,
  Content,
  Portal,
  Title,
  Description,
  Trigger,
} = DrawerPrimitive;

const DialogOverlay = forwardRef<
  ElementRef<typeof Overlay>,
  ComponentPropsWithoutRef<typeof Overlay>
>(({ className, ...props }, ref) => (
  <Overlay
    ref={ref}
    className={cn(
      // position
      'z-modal fixed inset-0 bg-black/45',
      className
    )}
    {...props}
  />
));

DialogOverlay.displayName = Overlay.displayName;

const DrawerContent = forwardRef<
  ElementRef<typeof Content>,
  ComponentPropsWithoutRef<typeof Content> & {
    onBack?: () => void;
    hideCloseBtn?: boolean;
    renderHeader?: ({
      onBack,
      hideCloseBtn,
    }: {
      onBack?: () => void;
      hideCloseBtn?: boolean;
    }) => ReactNode;
  }
>(
  (
    {
      className,
      children,
      onBack,
      hideCloseBtn,
      renderHeader = renderDefaultHeader,
      ...props
    },
    ref
  ) => (
    <Portal>
      <DialogOverlay />
      <Content
        ref={ref}
        className={cn(
          'z-modal fixed inset-x-0 bottom-0 mt-24 !touch-pan-y rounded-t',
          // layout
          'box-border flex max-h-[calc(100%-1.5rem)] flex-col gap-8 overflow-auto bg-white px-4 py-6 after:hidden',
          className
        )}
        {...props}
      >
        {renderHeader({ onBack, hideCloseBtn })}
        {children}
      </Content>
    </Portal>
  )
);
DrawerContent.displayName = Content.displayName;

const DrawerHeader = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-6', className)} {...props} />
);
DrawerHeader.displayName = 'DrawerHeader';

const DrawerFooter = ({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex size-full flex-col items-center justify-end gap-2',
      className
    )}
    {...props}
  >
    {Children.map(children as ReactElement<{ className?: string }>, (child) =>
      // Conditionally-rendered footer actions (`{cond ? <Button/> : null}`)
      // are a common, valid pattern — cloneElement throws on anything that
      // isn't a real element (null, but also a stray string/number child).
      isValidElement<{ className?: string }>(child)
        ? cloneElement(child, {
            className: cn(child.props.className, 'w-full'),
          })
        : child
    )}
  </div>
);
DrawerFooter.displayName = 'DrawerFooter';

const DrawerTitle = forwardRef<
  ElementRef<typeof Title>,
  ComponentPropsWithoutRef<typeof Title>
>(({ className, ...props }, ref) => (
  <Title
    ref={ref}
    className={cn('mobile-t4-semibold md:web-t4-semibold', className)}
    {...props}
  />
));

DrawerTitle.displayName = Title.displayName;

const DrawerSubtitle = forwardRef<
  ElementRef<typeof Description>,
  ComponentPropsWithoutRef<typeof Description>
>(({ className, ...props }, ref) => (
  <Description
    ref={ref}
    className={cn('body-16-light', className)}
    {...props}
  />
));
DrawerSubtitle.displayName = Description.displayName;

export {
  DrawerRoot,
  Trigger as DrawerTrigger,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerSubtitle,
};
